/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * GA4 ECOMMERCE EVENT TRACKING
 * ------------------------------------------------------------------------------------------------------------
 * 
 * Dit script luistert naar Focal theme events en pusht GA4 ecommerce events naar de dataLayer.
 * 
 * Events:
 * - add_to_cart: Wanneer een product wordt toegevoegd via variant:added
 * - remove_from_cart: Wanneer een product wordt verwijderd uit de cart
 * - begin_checkout: Wanneer de checkout knop wordt geklikt
 * - select_item: Wanneer een product wordt aangeklikt in een lijst
 * 
 * Prijzen worden berekend excl. 21% BTW.
 * 
 * Gemigreerd vanuit Lightspeed webshop - januari 2026
 */
(function() {
  'use strict';
  
  const BTW_RATE = 1.21;
  const CURRENCY = 'EUR';
  
  /**
   * Bereken prijs excl. BTW
   * @param {number} priceInCents - Prijs in centen (incl. BTW)
   * @returns {number} Prijs excl. BTW afgerond op 2 decimalen
   */
  function calculateExclVat(priceInCents) {
    return Math.round((priceInCents / 100 / BTW_RATE) * 100) / 100;
  }
  
  /**
   * Track add_to_cart event
   */
  document.addEventListener('variant:added', function(event) {
    const variant = event.detail.variant;
    const quantity = event.detail.quantity || 1;
    
    if (!variant) return;
    
    const priceExcl = calculateExclVat(variant.price);
    
    window.dataLayer = window.dataLayer || [];
    
    // Clear ecommerce object
    dataLayer.push({ ecommerce: null });
    
    // Push add_to_cart event
    dataLayer.push({
      'event': 'add_to_cart',
      'ecommerce': {
        'currency': CURRENCY,
        'value': priceExcl * quantity,
        'items': [{
          'item_id': String(variant.product_id),
          'item_name': variant.product_title || variant.name,
          'item_brand': variant.vendor || '',
          'item_category': variant.product_type || '',
          'item_variant': String(variant.id),
          'price': priceExcl,
          'quantity': quantity
        }]
      }
    });
    
    console.log('[GA4] add_to_cart:', variant.product_title, 'qty:', quantity);
  });
  
  /**
   * Track cart changes voor remove_from_cart
   * We vergelijken de oude cart met de nieuwe om te bepalen wat verwijderd is
   */
  let previousCart = null;
  
  document.addEventListener('cart:updated', function(event) {
    const cart = event.detail.cart;
    
    if (!cart || !previousCart) {
      previousCart = cart;
      return;
    }
    
    // Vergelijk items om te zien wat verwijderd of verminderd is
    const previousItems = {};
    previousCart.items.forEach(item => {
      previousItems[item.key] = item;
    });
    
    const currentItems = {};
    cart.items.forEach(item => {
      currentItems[item.key] = item;
    });
    
    // Check voor verwijderde of verminderde items
    Object.keys(previousItems).forEach(key => {
      const prevItem = previousItems[key];
      const currItem = currentItems[key];
      
      let removedQty = 0;
      
      if (!currItem) {
        // Item volledig verwijderd
        removedQty = prevItem.quantity;
      } else if (currItem.quantity < prevItem.quantity) {
        // Quantity verminderd
        removedQty = prevItem.quantity - currItem.quantity;
      }
      
      if (removedQty > 0) {
        const priceExcl = calculateExclVat(prevItem.price);
        
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });
        
        dataLayer.push({
          'event': 'remove_from_cart',
          'ecommerce': {
            'currency': CURRENCY,
            'value': priceExcl * removedQty,
            'items': [{
              'item_id': String(prevItem.product_id),
              'item_name': prevItem.product_title,
              'item_variant': String(prevItem.variant_id),
              'item_brand': prevItem.vendor,
              'price': priceExcl,
              'quantity': removedQty
            }]
          }
        });
        
        console.log('[GA4] remove_from_cart:', prevItem.product_title, 'qty:', removedQty);
      }
    });
    
    previousCart = cart;
  });
  
  /**
   * Track begin_checkout event
   * Luistert naar checkout knoppen
   */
  function trackBeginCheckout() {
    // Checkout knoppen in mini-cart en cart pagina
    const checkoutButtons = document.querySelectorAll('[name="checkout"], [href*="/checkout"], .shopify-payment-button button, form[action*="/checkout"] button[type="submit"]');
    
    checkoutButtons.forEach(button => {
      if (button.hasAttribute('data-ga4-checkout-tracked')) return;
      button.setAttribute('data-ga4-checkout-tracked', 'true');
      
      button.addEventListener('click', function() {
        // Haal cart data op via Shopify AJAX API
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            if (!cart.items || cart.items.length === 0) return;
            
            const cartTotalExcl = calculateExclVat(cart.total_price);
            
            const items = cart.items.map((item, index) => ({
              'item_id': String(item.product_id),
              'item_name': item.product_title,
              'item_variant': String(item.variant_id),
              'item_brand': item.vendor,
              'price': calculateExclVat(item.price),
              'quantity': item.quantity,
              'index': index
            }));
            
            window.dataLayer = window.dataLayer || [];
            dataLayer.push({ ecommerce: null });
            
            dataLayer.push({
              'event': 'begin_checkout',
              'ecommerce': {
                'currency': CURRENCY,
                'value': cartTotalExcl,
                'items': items
              }
            });
            
            console.log('[GA4] begin_checkout, value:', cartTotalExcl);
          })
          .catch(err => console.error('[GA4] Error fetching cart:', err));
      });
    });
  }
  
  /**
   * Track select_item event
   * Wanneer een product wordt aangeklikt in een lijst/grid
   */
  function trackSelectItem() {
    // Product links in collecties en zoekresultaten
    const productLinks = document.querySelectorAll('.product-item a[href*="/products/"], .product-list a[href*="/products/"]');
    
    productLinks.forEach(link => {
      if (link.hasAttribute('data-ga4-select-tracked')) return;
      link.setAttribute('data-ga4-select-tracked', 'true');
      
      link.addEventListener('click', function(e) {
        // Zoek product data in parent element
        const productItem = link.closest('.product-item, [data-product-id]');
        if (!productItem) return;
        
        // Probeer product ID te vinden
        const productId = productItem.dataset.productId || 
                         link.href.match(/\/products\/([^/?]+)/)?.[1] || 
                         '';
        
        // Probeer product naam te vinden
        const titleElement = productItem.querySelector('.product-item__title, .product-card__title, h3, h4');
        const productName = titleElement ? titleElement.textContent.trim() : '';
        
        // Probeer prijs te vinden
        const priceElement = productItem.querySelector('.price, .product-item__price');
        let price = 0;
        if (priceElement) {
          const priceText = priceElement.textContent.replace(/[^0-9,.\-]/g, '');
          price = parseFloat(priceText.replace(',', '.')) || 0;
        }
        
        // Probeer list name te bepalen
        const listContainer = link.closest('[data-collection-id], .collection, .search-results');
        const listName = listContainer?.dataset?.collectionTitle || 
                        document.querySelector('h1')?.textContent.trim() || 
                        'Product List';
        
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });
        
        dataLayer.push({
          'event': 'select_item',
          'ecommerce': {
            'currency': CURRENCY,
            'item_list_name': listName,
            'items': [{
              'item_id': productId,
              'item_name': productName,
              'price': price > 0 ? Math.round((price / BTW_RATE) * 100) / 100 : 0,
              'item_list_name': listName
            }]
          }
        });
        
        console.log('[GA4] select_item:', productName);
      });
    });
  }
  
  /**
   * Initialiseer tracking
   */
  function initGA4Tracking() {
    trackBeginCheckout();
    trackSelectItem();
  }
  
  // Start tracking wanneer DOM geladen is
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGA4Tracking);
  } else {
    initGA4Tracking();
  }
  
  // Herinitialiseer na cart updates (nieuwe checkout knoppen kunnen verschijnen)
  document.addEventListener('cart:updated', function() {
    setTimeout(initGA4Tracking, 500);
  });
  
  // Herinitialiseer na dynamische content loads
  const observer = new MutationObserver(function(mutations) {
    let shouldReinit = false;
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (
            node.querySelector?.('[name="checkout"]') ||
            node.querySelector?.('.product-item') ||
            node.matches?.('[name="checkout"]') ||
            node.matches?.('.product-item')
          )) {
            shouldReinit = true;
          }
        });
      }
    });
    if (shouldReinit) {
      setTimeout(initGA4Tracking, 100);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
  
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * AUTOMATISCHE EX-BTW PRIJS BEREKENING (v2 - Optimized)
 * ------------------------------------------------------------------------------------------------------------
 * 
 * Dit script observeert de incl. BTW prijs en berekent automatisch de ex-BTW prijs.
 * Wanneer Globo Product Options (of een andere app) de incl. BTW prijs wijzigt,
 * wordt de ex-BTW prijs automatisch bijgewerkt.
 * 
 * Optimizations v2:
 * - Removed 500ms polling interval (was causing unnecessary CPU usage)
 * - Single document-level MutationObserver instead of per-wrapper observers
 * - IntersectionObserver for lazy-loaded content (recommendations, quick-buy)
 * - requestAnimationFrame for smooth paint-aligned updates
 * - Compare-at price support
 * - Data attribute caching to prevent redundant DOM writes
 * 
 * BTW percentage: 21%
 */
(function() {
  const BTW_RATE = 1.21;
  let pendingUpdate = null;
  let documentObserver = null;
  let intersectionObserver = null;
  
  /**
   * Parse een prijs string naar cents (bijv. "€369,00" -> 36900)
   */
  function parsePriceToCents(priceString) {
    if (!priceString) return 0;
    // Verwijder alles behalve cijfers, komma en punt
    let cleaned = priceString.replace(/[^0-9,.\-]/g, '').trim();
    if (!cleaned) return 0;
    
    // Converteer Europese notatie (1.234,56) naar standaard (1234.56)
    if (cleaned.includes(',')) {
      // Check of het een Europese notatie is (komma als decimaal)
      if (cleaned.indexOf(',') > cleaned.indexOf('.') || !cleaned.includes('.')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      }
    }
    const value = parseFloat(cleaned);
    return isNaN(value) ? 0 : Math.round(value * 100);
  }
  
  /**
   * Formatteer cents naar een prijs string met Nederlandse notatie (bijv. 30500 -> "€305,00")
   */
  function formatMoney(cents) {
    const euros = cents / 100;
    // Nederlandse notatie: punt als duizendtallen separator, komma als decimaal
    const formatted = euros.toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return '€' + formatted;
  }
  
  /**
   * Update a single excl price element based on its source incl price
   * @param {Element} exclElement - The element to update (data-price-excl-amount or data-compare-excl-amount)
   * @param {number} inclCents - The incl. BTW price in cents
   * @param {string} cacheAttr - Data attribute name for caching (e.g., 'data-last-incl')
   */
  function updateSingleExclElement(exclElement, inclCents, cacheAttr) {
    if (!exclElement || inclCents === 0) return;
    
    // Check cache to avoid redundant DOM writes
    const cachedIncl = parseInt(exclElement.getAttribute(cacheAttr) || '0', 10);
    if (cachedIncl === inclCents) return;
    
    const exclCents = Math.round(inclCents / BTW_RATE);
    const newExclPrice = formatMoney(exclCents);
    
    // Only update if actually different
    if (exclElement.textContent.trim() !== newExclPrice) {
      exclElement.textContent = newExclPrice;
    }
    
    // Cache the source incl price to prevent recalculation
    exclElement.setAttribute(cacheAttr, inclCents.toString());
  }
  
  /**
   * Update de ex-BTW prijs gebaseerd op de incl-BTW prijs
   */
  function updateExclPrice(wrapper) {
    // Zoek de incl-BTW prijs wrapper
    const inclWrapper = wrapper.querySelector('[data-price-incl]');
    if (!inclWrapper) return;
    
    // Zoek de prijs in het incl wrapper - Globo kan de .price element updaten
    const priceElement = inclWrapper.querySelector('.price') || inclWrapper;
    const priceText = priceElement.textContent;
    const inclCents = parsePriceToCents(priceText);
    
    // Update main excl price
    const exclElement = wrapper.querySelector('[data-price-excl-amount]');
    updateSingleExclElement(exclElement, inclCents, 'data-last-incl');
    
    // Update compare-at excl price if it exists
    // Note: Compare-at price typically doesn't change with Globo options,
    // but we handle it for completeness (e.g., if theme re-renders)
    const compareExclElement = wrapper.querySelector('[data-compare-excl-amount]');
    if (compareExclElement) {
      // Compare price is static from Liquid, read from wrapper data or element
      const compareInclElement = wrapper.querySelector('[data-compare-incl-amount]');
      if (compareInclElement) {
        const compareInclCents = parsePriceToCents(compareInclElement.textContent);
        updateSingleExclElement(compareExclElement, compareInclCents, 'data-last-compare-incl');
      }
    }
  }
  
  /**
   * Update all price wrappers in the document
   */
  function updateAllPrices() {
    document.querySelectorAll('[data-price-wrapper]').forEach(wrapper => {
      updateExclPrice(wrapper);
    });
  }
  
  /**
   * Schedule a batched update using requestAnimationFrame
   * This debounces rapid updates and aligns with browser paint cycle
   */
  function scheduleUpdate() {
    if (pendingUpdate) return;
    pendingUpdate = requestAnimationFrame(() => {
      pendingUpdate = null;
      updateAllPrices();
    });
  }
  
  /**
   * Set up a single document-level MutationObserver
   * Watches for .price element changes anywhere in the document
   */
  function setupDocumentObserver() {
    if (documentObserver) return;
    
    documentObserver = new MutationObserver((mutations) => {
      // Check if any mutation affects a price element
      const hasPriceChange = mutations.some(mutation => {
        // Check if the mutation target is or contains a price element
        if (mutation.target.classList?.contains('price')) return true;
        if (mutation.target.closest?.('[data-price-wrapper]')) return true;
        
        // Check added nodes
        if (mutation.addedNodes.length) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) {
              if (node.classList?.contains('price') || 
                  node.querySelector?.('.price') ||
                  node.matches?.('[data-price-wrapper]') ||
                  node.querySelector?.('[data-price-wrapper]')) {
                return true;
              }
            }
          }
        }
        return false;
      });
      
      if (hasPriceChange) {
        scheduleUpdate();
      }
    });
    
    // Observe document body for all price changes
    documentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }
  
  /**
   * Set up IntersectionObserver for lazy-loaded content
   * Handles prices in recommendations, quick-buy, etc.
   */
  function setupIntersectionObserver() {
    if (intersectionObserver) return;
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers: just update all on scroll
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      return;
    }
    
    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          updateExclPrice(entry.target);
          // Optionally unobserve after first update if content is static
          // intersectionObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '100px' // Start updating slightly before visible
    });
    
    // Observe all current price wrappers
    document.querySelectorAll('[data-price-wrapper]').forEach(wrapper => {
      intersectionObserver.observe(wrapper);
    });
    
    // Re-observe new wrappers when they're added
    const addObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.matches?.('[data-price-wrapper]')) {
              intersectionObserver.observe(node);
            }
            node.querySelectorAll?.('[data-price-wrapper]').forEach(wrapper => {
              intersectionObserver.observe(wrapper);
            });
          }
        });
      });
    });
    
    addObserver.observe(document.body, { childList: true, subtree: true });
  }
  
  /**
   * Initialize all observers and perform initial update
   */
  function init() {
    // Initial update for all visible prices
    updateAllPrices();
    
    // Set up observers
    setupDocumentObserver();
    setupIntersectionObserver();
  }
  
  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Handle variant changes - use requestAnimationFrame for smooth updates
  document.addEventListener('variant:changed', () => {
    // Wait for DOM to settle after variant change, then update
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateAllPrices();
      });
    });
  });
  
  // Handle product re-renders (theme replaces entire meta block)
  document.addEventListener('product:rerender', () => {
    // Double RAF to ensure DOM is fully updated after re-render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateAllPrices();
        // Re-observe any new price wrappers
        if (intersectionObserver) {
          document.querySelectorAll('[data-price-wrapper]').forEach(wrapper => {
            intersectionObserver.observe(wrapper);
          });
        }
      });
    });
  });
  
  // Handle cart updates (mini-cart, drawer)
  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);
})();